/**
 * LocalPDetailPage — chi tiết 1 hợp đồng Local Purchase.
 * Header (status chips) + actions (Edit / Copy / Force Close / Print menu / Delete) +
 * Tabs (General Info | Annexes | Forward Deposits | PTBF Fixations | Plan).
 */
import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import LockIcon from '@mui/icons-material/Lock'
import PrintIcon from '@mui/icons-material/Print'
import { useNavigate, useParams } from 'react-router-dom'

import ConfirmDialog from '@/components/ConfirmDialog'
import PageDataState from '@/components/PageDataState'
import RoleGuard from '@/components/RoleGuard'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useCopyLocalPMutation,
  useDeleteLocalPMutation,
  useForceCloseLocalPMutation,
  useGetLocalPQuery,
  useLazyPrintLocalPQuery,
} from '@/store/api/localPApi'
import type { LocalPDetail, LocalPPrintKind } from '@/types/api'
import { formatDate } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'
import { formatNumber } from '@/utils/money'

import LocalPFormDialog from './LocalPFormDialog'
import DepositFormDialog from './DepositFormDialog'
import PlanFormDialog from './PlanFormDialog'
import PTBFFixationFormDialog from './PTBFFixationFormDialog'
import PTBFRollingFormDialog from './PTBFRollingFormDialog'
import {
  useDeleteForwardMutation,
  useListForwardsQuery,
} from '@/store/api/localPForwardsApi'
import {
  useDeletePlanMutation,
  useListPlansQuery,
} from '@/store/api/localPPlansApi'
import {
  useDeletePTBFFixationMutation,
  useListPTBFFixationsQuery,
} from '@/store/api/ptbfFixationsApi'
import {
  useDeletePTBFRollingMutation,
  useListPTBFRollingsQuery,
} from '@/store/api/ptbfRollingsApi'
import DataTableMUI from '@/components/DataTableMUI'
import AddIcon from '@mui/icons-material/Add'
import type { GridColDef } from '@mui/x-data-grid'
import type {
  LocalPForwardOut,
  LocalPPlanOut,
  PTBFFixationOut,
  PTBFRollingOut,
} from '@/types/api'

const PRINT_KINDS: Array<{ kind: LocalPPrintKind; label: string }> = [
  { kind: 'contract', label: 'Contract' },
  { kind: 'contract-forward', label: 'Forward Contract' },
  { kind: 'cancel', label: 'Cancellation Note' },
  { kind: 'dispatch', label: 'Dispatch Note' },
]

interface FieldRowProps {
  label: string
  value: React.ReactNode
}
function FieldRow({ label, value }: FieldRowProps) {
  return (
    <Box display="flex" gap={2} sx={{ flex: '1 1 280px', minWidth: 0 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 140, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {value === null || value === undefined || value === '' ? '—' : value}
      </Typography>
    </Box>
  )
}

interface SectionProps {
  title: string
  children: React.ReactNode
}
function Section({ title, children }: SectionProps) {
  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <Typography variant="subtitle1" fontWeight={700} color="primary">
        {title}
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={2}>
        {children}
      </Box>
      <Divider />
    </Box>
  )
}

function GeneralInfoTab({ d }: { d: LocalPDetail }) {
  return (
    <Box display="flex" flexDirection="column" gap={3} pt={2}>
      <Section title="General">
        <FieldRow label="Contract No." value={d.contract_no} />
        <FieldRow label="Contract Date" value={formatDate(d.contract_date)} />
        <FieldRow label="Vendor" value={`${d.supplier_code ?? ''} — ${d.supplier_name ?? d.short_name ?? ''}`} />
        <FieldRow label="Seller Ref" value={d.seller_ref} />
        <FieldRow label="Quality Code" value={d.quality_code} />
        <FieldRow label="Certification" value={d.certification} />
        <FieldRow label="ME / IB" value={d.me_or_ib} />
        <FieldRow label="License Cert No" value={d.license_cert_no} />
        <FieldRow label="Quantity (kg)" value={formatNumber(d.quantity_kg, 0)} />
        <FieldRow label="No. of Bags" value={d.no_of_bag} />
        <FieldRow label="Package" value={d.package} />
      </Section>

      <Section title="Pricing">
        <FieldRow label="Contract Type" value={d.contract_type} />
        <FieldRow
          label="Outright Price"
          value={formatNumber(d.outright_price ?? null, 2)}
        />
        <FieldRow label="PTBF Diffs" value={formatNumber(d.ptbf_diffs ?? null, 2)} />
        <FieldRow label="Price Unit" value={d.price_unit} />
        <FieldRow label="VAT Rate" value={d.vat_rate} />
        <FieldRow label="FX" value={d.fx} />
        <FieldRow label="Fixed FX" value={d.fixed_fx} />
        <FieldRow label="Futures Month" value={d.futures_month} />
        <FieldRow label="Fixation Call" value={d.fixation_call} />
      </Section>

      <Section title="Logistics">
        <FieldRow label="Latest Delivery" value={formatDate(d.latest_delivery)} />
        <FieldRow label="Delivery Term" value={d.delivery_term} />
        <FieldRow label="Delivery Place" value={d.delivery_place_code} />
        <FieldRow label="Logistic Cost" value={formatNumber(d.logistic_cost ?? null, 2)} />
        <FieldRow label="Trader" value={d.trader_in_charge} />
      </Section>

      <Section title="Forward / Deposit">
        <FieldRow label="Provi Market Rate" value={d.provi_market_rate} />
        <FieldRow label="Stop-loss Rate" value={d.stoploss_rate} />
        <FieldRow label="Deposit Value %" value={d.margin_value_per} />
        <FieldRow label="Deposit Limit %" value={d.margin_limit_per} />
        <FieldRow label="Time Limit (d)" value={d.time_limit} />
        <FieldRow label="Bonus %" value={d.margin_bonus_per} />
        <FieldRow label="Interest %/m" value={d.margin_interest_month} />
        <FieldRow
          label="Repayment Date"
          value={formatDate(d.deposit_repayment_date)}
        />
      </Section>

      {d.is_intermediary && (
        <Section title="Intermediary">
          <FieldRow label="Shipper" value={d.shipper_code} />
          <FieldRow
            label="Contract Shipper"
            value={d.contract_shipper_intermediary}
          />
          <FieldRow
            label="Outright Shipper"
            value={formatNumber(d.outright_shipper_intermediary ?? null, 2)}
          />
          <FieldRow
            label="PTBF Diffs Shipper"
            value={formatNumber(d.ptbfdiffs_shipper_intermediary ?? null, 2)}
          />
        </Section>
      )}

      <Section title="Workflow flags">
        <FieldRow label="Issued" value={d.is_issued ? 'Yes' : 'No'} />
        <FieldRow label="Sent Mail" value={d.is_sent_mail ? 'Yes' : 'No'} />
        <FieldRow label="Received Mail" value={d.is_received_mail ? 'Yes' : 'No'} />
        <FieldRow label="Sent Paper" value={d.is_sent_paper ? 'Yes' : 'No'} />
        <FieldRow label="Received Paper" value={d.is_received_paper ? 'Yes' : 'No'} />
      </Section>

      <Section title="Notes">
        <FieldRow label="Note" value={d.note} />
        <FieldRow label="Quality Description" value={d.quality_des_contract} />
      </Section>
    </Box>
  )
}

function PlaceholderTab({ message }: { message: string }) {
  return (
    <Box p={3} display="flex" justifyContent="center">
      <Typography color="text.secondary">{message}</Typography>
    </Box>
  )
}

/** Nested tab — Forward Deposits của 1 contract. */
function ForwardDepositsTab({ contractNo }: { contractNo: string }) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const [addOpen, setAddOpen] = useState(false)
  const [editing, setEditing] = useState<LocalPForwardOut | null>(null)
  const [confirmDel, setConfirmDel] = useState<LocalPForwardOut | null>(null)

  const { data, isLoading, error, isFetching } = useListForwardsQuery({
    contract_no: contractNo,
    page: 1,
    page_size: 1_000_000,
  })
  const [deleteForward, { isLoading: deleting }] = useDeleteForwardMutation()

  const rows = useMemo<(LocalPForwardOut & { id: number })[]>(
    () => (data?.items ?? []).map((f) => ({ ...f })),
    [data],
  )

  const columns = useMemo<GridColDef<LocalPForwardOut & { id: number }>[]>(
    () => [
      {
        field: 'payment_date',
        headerName: 'Payment Date',
        width: 110,
        valueGetter: (_v, row) => formatDate(row.payment_date),
      },
      { field: 'forward_type', headerName: 'Type', width: 130 },
      {
        field: 'quantity',
        headerName: 'Qty',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.quantity ?? null, 0),
      },
      {
        field: 'price',
        headerName: 'Price',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.price ?? null, 2),
      },
      {
        field: 'fx',
        headerName: 'FX',
        width: 100,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.fx ?? null, 2),
      },
      {
        field: 'amount',
        headerName: 'Amount (VND)',
        width: 150,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.amount ?? null, 0),
      },
      {
        field: 'interest_value',
        headerName: 'Interest',
        width: 130,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.interest_value ?? null, 0),
      },
      { field: 'note', headerName: 'Note', flex: 1, minWidth: 160 },
      {
        field: '_actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" disabled={!canEdit} onClick={() => setEditing(p.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              disabled={!canEdit}
              onClick={() => setConfirmDel(p.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleConfirmDelete = async () => {
    if (!confirmDel) return
    try {
      await deleteForward(confirmDel.id).unwrap()
      snack.success('Deposit deleted')
      setConfirmDel(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to delete deposit'))
    }
  }

  const isEmpty = !isLoading && !error && rows.length === 0

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={2}>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          disabled={!canEdit}
          onClick={() => setAddOpen(true)}
        >
          Thêm Deposit
        </Button>
      </Box>
      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <DataTableMUI rows={rows} columns={columns as GridColDef[]} loading={isFetching} height={420} />
      </PageDataState>
      <DepositFormDialog
        open={addOpen}
        mode="create"
        fixedContractNo={contractNo}
        onClose={() => setAddOpen(false)}
      />
      <DepositFormDialog
        open={!!editing}
        mode="edit"
        fixedContractNo={contractNo}
        initialValue={editing}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        open={!!confirmDel}
        title="Delete deposit"
        message={
          confirmDel
            ? `Delete deposit ${confirmDel.forward_type} on ${formatDate(confirmDel.payment_date)}?`
            : ''
        }
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDel(null)}
      />
    </Box>
  )
}

/** Nested tab — Plan của 1 contract. */
function PlanTab({ contractNo }: { contractNo: string }) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const [addOpen, setAddOpen] = useState(false)
  const [editing, setEditing] = useState<LocalPPlanOut | null>(null)
  const [confirmDel, setConfirmDel] = useState<LocalPPlanOut | null>(null)

  const { data, isLoading, error, isFetching } = useListPlansQuery({
    contract_no: contractNo,
    page: 1,
    page_size: 1_000_000,
  })
  const [deletePlan, { isLoading: deleting }] = useDeletePlanMutation()

  // listPlans giờ trả `LocalPPlanListItem` từ view `v_local_p_plan`. View bao
  // gồm estimate rows (id=null) — chỉ giữ rows có base `local_p_plan` cho tab
  // contract-detail (Edit/Delete cần id) và map về shape `LocalPPlanOut` cho
  // các dialog tái sử dụng.
  const rows = useMemo<(LocalPPlanOut & { id: number })[]>(
    () =>
      (data?.items ?? [])
        .filter((p): p is typeof p & { id: number } => p.id != null)
        .map((p) => ({
          id: p.id,
          contract_no: p.contract_no,
          name: p.cp_name,
          check_date: p.check_date,
          plan_quant: p.plan_quant,
          note: p.note,
        })),
    [data],
  )

  const columns = useMemo<GridColDef<LocalPPlanOut & { id: number }>[]>(
    () => [
      {
        field: 'check_date',
        headerName: 'Plan Date',
        width: 130,
        valueGetter: (_v, row) => formatDate(row.check_date),
      },
      {
        field: 'plan_quant',
        headerName: 'SL Plan (kg)',
        width: 140,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.plan_quant, 0),
      },
      { field: 'note', headerName: 'Note', flex: 1, minWidth: 200 },
      {
        field: '_actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" disabled={!canEdit} onClick={() => setEditing(p.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              disabled={!canEdit}
              onClick={() => setConfirmDel(p.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleConfirmDelete = async () => {
    if (!confirmDel) return
    try {
      await deletePlan(confirmDel.id).unwrap()
      snack.success('Plan deleted')
      setConfirmDel(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to delete plan'))
    }
  }

  const isEmpty = !isLoading && !error && rows.length === 0

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={2}>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          disabled={!canEdit}
          onClick={() => setAddOpen(true)}
        >
          Thêm Plan
        </Button>
      </Box>
      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <DataTableMUI rows={rows} columns={columns as GridColDef[]} loading={isFetching} height={420} />
      </PageDataState>
      <PlanFormDialog
        open={addOpen}
        mode="create"
        fixedContractNo={contractNo}
        onClose={() => setAddOpen(false)}
      />
      <PlanFormDialog
        open={!!editing}
        mode="edit"
        fixedContractNo={contractNo}
        initialValue={editing}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        open={!!confirmDel}
        title="Delete plan"
        message={
          confirmDel
            ? `Delete plan on ${formatDate(confirmDel.check_date)} for ${confirmDel.contract_no}?`
            : ''
        }
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDel(null)}
      />
    </Box>
  )
}

/** Nested tab — PTBF Fixations của 1 contract. */
function PTBFFixationsTab({ contractNo }: { contractNo: string }) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const [addOpen, setAddOpen] = useState(false)
  const [editing, setEditing] = useState<PTBFFixationOut | null>(null)
  const [confirmDel, setConfirmDel] = useState<PTBFFixationOut | null>(null)

  const { data, isLoading, error, isFetching } = useListPTBFFixationsQuery({
    contract_no: contractNo,
    page: 1,
    page_size: 1_000_000,
  })
  const [deleteFixation, { isLoading: deleting }] = useDeletePTBFFixationMutation()

  const rows = useMemo<(PTBFFixationOut & { id: number })[]>(
    () => (data?.items ?? []).map((f) => ({ id: f.ptbf_p_id, ...f })),
    [data],
  )

  const columns = useMemo<GridColDef<PTBFFixationOut & { id: number }>[]>(
    () => [
      {
        field: 'fixation_date',
        headerName: 'Date',
        width: 110,
        valueGetter: (_v, row) => formatDate(row.fixation_date),
      },
      {
        field: 'lot_fixed',
        headerName: 'Lot Fixed',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.lot_fixed ?? null, 0),
      },
      {
        field: 'market_level',
        headerName: 'Market Level',
        width: 130,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.market_level ?? null, 4),
      },
      { field: 'futures_month', headerName: 'Futures', width: 100 },
      {
        field: 'final_price',
        headerName: 'Final Price',
        width: 130,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.final_price ?? null, 4),
      },
      {
        field: 'is_upload',
        headerName: 'Uploaded',
        width: 100,
        renderCell: (p) =>
          p.row.is_upload ? (
            <Chip size="small" label="Yes" color="success" />
          ) : (
            <Chip size="small" label="No" />
          ),
      },
      { field: 'note', headerName: 'Note', flex: 1, minWidth: 160 },
      {
        field: '_actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              disabled={!!p.row.is_upload || !canEdit}
              onClick={() => setEditing(p.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              disabled={!!p.row.is_upload || !canEdit}
              onClick={() => setConfirmDel(p.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleConfirmDelete = async () => {
    if (!confirmDel) return
    try {
      await deleteFixation(confirmDel.ptbf_p_id).unwrap()
      snack.success('Fixation deleted')
      setConfirmDel(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to delete fixation'))
    }
  }

  const isEmpty = !isLoading && !error && rows.length === 0

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={2}>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          disabled={!canEdit}
          onClick={() => setAddOpen(true)}
        >
          + Add Fixation
        </Button>
      </Box>
      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <DataTableMUI rows={rows} columns={columns as GridColDef[]} loading={isFetching} height={420} />
      </PageDataState>
      <PTBFFixationFormDialog
        open={addOpen}
        mode="create"
        fixedContractNo={contractNo}
        onClose={() => setAddOpen(false)}
      />
      <PTBFFixationFormDialog
        open={!!editing}
        mode="edit"
        fixedContractNo={contractNo}
        initialValue={editing}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        open={!!confirmDel}
        title="Delete fixation"
        message={
          confirmDel
            ? `Delete fixation #${confirmDel.ptbf_p_id} on ${formatDate(confirmDel.fixation_date)}?`
            : ''
        }
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDel(null)}
      />
    </Box>
  )
}

/** Nested tab — PTBF Rolling của 1 contract. */
function PTBFRollingTab({ contractNo }: { contractNo: string }) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const [addOpen, setAddOpen] = useState(false)
  const [editing, setEditing] = useState<PTBFRollingOut | null>(null)
  const [confirmDel, setConfirmDel] = useState<PTBFRollingOut | null>(null)

  const { data, isLoading, error, isFetching } = useListPTBFRollingsQuery({
    contract_no: contractNo,
    page: 1,
    page_size: 1_000_000,
  })
  const [deleteRolling, { isLoading: deleting }] = useDeletePTBFRollingMutation()

  const rows = useMemo<(PTBFRollingOut & { id: number })[]>(
    () => (data?.items ?? []).map((r) => ({ ...r })),
    [data],
  )

  const columns = useMemo<GridColDef<PTBFRollingOut & { id: number }>[]>(
    () => [
      {
        field: 'rolling_date',
        headerName: 'Rolling Date',
        width: 120,
        valueGetter: (_v, row) => formatDate(row.rolling_date),
      },
      {
        field: 'lot_to_roll',
        headerName: 'Lot To Roll',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.lot_to_roll ?? null, 0),
      },
      {
        field: '_futures',
        headerName: 'Futures Old → New',
        width: 160,
        valueGetter: (_v, row) =>
          `${row.old_futures_month ?? ''} → ${row.new_futures_month ?? ''}`,
      },
      {
        field: 'spread',
        headerName: 'Spread',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.spread ?? null, 4),
      },
      {
        field: 'rolling_fee',
        headerName: 'Rolling Fee',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_v, row) => formatNumber(row.rolling_fee ?? null, 4),
      },
      { field: 'ref_amendment', headerName: 'Ref Amendment', width: 130 },
      { field: 'note', headerName: 'Note', flex: 1, minWidth: 160 },
      {
        field: '_actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" disabled={!canEdit} onClick={() => setEditing(p.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              disabled={!canEdit}
              onClick={() => setConfirmDel(p.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleConfirmDelete = async () => {
    if (!confirmDel) return
    try {
      await deleteRolling(confirmDel.id).unwrap()
      snack.success('Rolling deleted')
      setConfirmDel(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to delete rolling'))
    }
  }

  const isEmpty = !isLoading && !error && rows.length === 0

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={2}>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          disabled={!canEdit}
          onClick={() => setAddOpen(true)}
        >
          + Add Rolling
        </Button>
      </Box>
      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <DataTableMUI rows={rows} columns={columns as GridColDef[]} loading={isFetching} height={420} />
      </PageDataState>
      <PTBFRollingFormDialog
        open={addOpen}
        mode="create"
        fixedContractNo={contractNo}
        onClose={() => setAddOpen(false)}
      />
      <PTBFRollingFormDialog
        open={!!editing}
        mode="edit"
        fixedContractNo={contractNo}
        initialValue={editing}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        open={!!confirmDel}
        title="Delete rolling"
        message={
          confirmDel
            ? `Delete rolling #${confirmDel.id} on ${formatDate(confirmDel.rolling_date)}?`
            : ''
        }
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDel(null)}
      />
    </Box>
  )
}

const TAB_LABELS = [
  'General Info',
  'Amendments',
  'Forward Deposits',
  'PTBF Fixations',
  'PTBF Rolling',
  'Plan',
]

export function LocalPDetailPage() {
  const { canEdit } = usePagePermission()
  const { contractNo = '' } = useParams<{ contractNo: string }>()
  const navigate = useNavigate()
  const snack = useSnackbar()

  const { data, isLoading, error, refetch } = useGetLocalPQuery(contractNo, {
    skip: !contractNo,
  })
  const detail = data?.data

  const [tab, setTab] = useState(0)
  const [editOpen, setEditOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmForceClose, setConfirmForceClose] = useState(false)
  const [printAnchor, setPrintAnchor] = useState<HTMLElement | null>(null)

  const [deleteLocalP, { isLoading: deleting }] = useDeleteLocalPMutation()
  const [copyLocalP] = useCopyLocalPMutation()
  const [forceClose, { isLoading: forcingClose }] = useForceCloseLocalPMutation()
  const [triggerPrint] = useLazyPrintLocalPQuery()

  const statusChips = useMemo(() => {
    if (!detail) return null
    const chips: React.ReactNode[] = []
    if (detail.is_status === 0)
      chips.push(<Chip key="cancelled" size="small" label="Cancelled" />)
    if (detail.is_forced_close)
      chips.push(
        <Chip key="forced" size="small" label="Forced-close" color="warning" />,
      )
    if (detail.is_status !== 0 && !detail.is_forced_close)
      chips.push(<Chip key="active" size="small" label="Active" color="success" />)
    if (detail.is_intermediary)
      chips.push(<Chip key="inter" size="small" label="Intermediary" color="info" />)
    return chips
  }, [detail])

  const handleCopy = async () => {
    if (!detail) return
    try {
      const res = await copyLocalP(detail.contract_no).unwrap()
      snack.success(res.message ?? 'Contract copied')
      const newNo = res.data?.contract_no
      if (newNo) navigate(`/purchase/local-p/${encodeURIComponent(newNo)}`)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to copy contract'))
    }
  }

  const handleConfirmDelete = async () => {
    if (!detail) return
    try {
      await deleteLocalP(detail.contract_no).unwrap()
      snack.success(`Deleted ${detail.contract_no}`)
      setConfirmDelete(false)
      navigate('/purchase/local-p')
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to delete contract'))
    }
  }

  const handleConfirmForceClose = async () => {
    if (!detail) return
    try {
      await forceClose({ contractNo: detail.contract_no }).unwrap()
      snack.success(`Force-closed ${detail.contract_no}`)
      setConfirmForceClose(false)
      void refetch()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Force-close failed'))
    }
  }

  const handlePrint = async (kind: LocalPPrintKind) => {
    if (!detail) return
    setPrintAnchor(null)
    try {
      await triggerPrint({ contractNo: detail.contract_no, kind }).unwrap()
      snack.info(`Print request (${kind}) sent — placeholder Sprint 6`)
    } catch (err) {
      snack.error(getErrorMessage(err, 'PDF print failed'))
    }
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Tooltip title="Back">
          <IconButton onClick={() => navigate('/purchase/local-p')}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h5" fontWeight={700}>
          {detail?.contract_no ?? contractNo}
        </Typography>
        {detail?.supplier_name || detail?.short_name ? (
          <Typography variant="subtitle1" color="text.secondary">
            — {detail?.supplier_name ?? detail?.short_name}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={1}>
          {statusChips}
        </Stack>
        <Box flex={1} />
        {detail && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              disabled={!canEdit}
              onClick={() => setEditOpen(true)}
            >
              Sửa
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              disabled={!canEdit}
              onClick={() => void handleCopy()}
            >
              Copy
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<LockIcon />}
              disabled={!!detail.is_forced_close || !canEdit}
              onClick={() => setConfirmForceClose(true)}
            >
              Force Close
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={(e) => setPrintAnchor(e.currentTarget)}
            >
              In PDF
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={!canEdit}
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </Button>
          </Stack>
        )}
      </Box>

      <PageDataState loading={isLoading} error={error} empty={!isLoading && !detail}>
        {detail ? (
          <Paper sx={{ p: 2 }}>
            <Tabs
              value={tab}
              onChange={(_e, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {TAB_LABELS.map((l) => (
                <Tab key={l} label={l} />
              ))}
            </Tabs>
            <Box>
              {tab === 0 && <GeneralInfoTab d={detail} />}
              {tab === 1 && <PlaceholderTab message="Amendments — to be implemented in Sprint 5" />}
              {tab === 2 && <ForwardDepositsTab contractNo={detail.contract_no} />}
              {tab === 3 && <PTBFFixationsTab contractNo={detail.contract_no} />}
              {tab === 4 && <PTBFRollingTab contractNo={detail.contract_no} />}
              {tab === 5 && <PlanTab contractNo={detail.contract_no} />}
            </Box>
          </Paper>
        ) : null}
      </PageDataState>

      <LocalPFormDialog
        open={editOpen}
        mode="edit"
        initialValue={detail ?? null}
        onClose={() => setEditOpen(false)}
        onSaved={() => void refetch()}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete contract"
        message={`Are you sure you want to delete contract ${detail?.contract_no ?? ''}?`}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(false)}
      />

      <ConfirmDialog
        open={confirmForceClose}
        title="Force-close contract"
        message={`Are you sure you want to force-close contract ${detail?.contract_no ?? ''}?`}
        destructive
        loading={forcingClose}
        onConfirm={() => void handleConfirmForceClose()}
        onCancel={() => setConfirmForceClose(false)}
      />

      <Menu
        anchorEl={printAnchor}
        open={!!printAnchor}
        onClose={() => setPrintAnchor(null)}
      >
        {PRINT_KINDS.map((k) => (
          <MenuItem key={k.kind} onClick={() => void handlePrint(k.kind)}>
            {k.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

export default LocalPDetailPage
